import styles from "./OrderSettings.module.css";
import { useEffect, useState } from "react";
import CloseButton from "../../Button/CloseButton/CloseButton";
import SubmitButton from "../../Button/SubmitButton/SubmitButton";
import DatePicker from "../../DatePicker/DatePicker";
import AddressInput from "../../Input/AddressInput/AddressInput";
import { useOrderContext } from "../../../context/OrderContext";
import { useTranslation } from "react-i18next";
import { handleBackgroundScroll } from "../../../utils/backgroundScroll";
import { useUserContext } from "../../../context/UserContext";
import { useCartContext } from "../../../context/CartContext";
import { normalizeDate, filterDatesOnOrAfterEffectiveTomorrow } from "../../../utils/calendar";
import { useDeliveryAddresses } from "../../../hooks/useDeliveryAddresses";
import { useUpdateCartHandler } from "../../../hooks/useUpdateCartHandler";

interface OrderSettingsPopupProps {
    isOpen: boolean;
    setOpen: (val: boolean) => void;
    selectedDates: string[];
    setSelectedDates: (values: string[]) => void;
}

const OrderSettingsPopup = ({ isOpen, setOpen, setSelectedDates }: OrderSettingsPopupProps) => {
    const { t } = useTranslation();
    const { orderData, handleOrder } = useOrderContext();
    const { userData } = useUserContext();
    const { cartData, cartLoaded } = useCartContext();

    const [selectedAddress, setSelectedAddress] = useState("");
    const [currSelectedDates, setCurrSelectedDates] = useState<string[]>([]);

    // Use consolidated hooks
    const subscriberId = cartData?.subscriber_id ?? orderData.recipient;
    const adresses = useDeliveryAddresses(subscriberId, userData);
    const { handleUpdateCart } = useUpdateCartHandler();

    useEffect(() => {
        handleBackgroundScroll(isOpen);
    }, [isOpen]);

    // Initial dates from cartData.selected_dates (D. M. YYYY -> ISO)
    useEffect(() => {
        const cartSelected = cartData?.selected_dates ?? [];
        if (Array.isArray(cartSelected) && cartSelected.length > 0) {
        setCurrSelectedDates(
            filterDatesOnOrAfterEffectiveTomorrow(cartSelected.map(normalizeDate))
        );
        return;
        }
        // fallback if cart not ready yet
        setCurrSelectedDates(filterDatesOnOrAfterEffectiveTomorrow(orderData.date ?? []));
    }, [cartData?.selected_dates]);

    // Drop dates that are no longer valid after cutoff
    useEffect(() => {
        if (currSelectedDates.length === 0) return;
        const filtered = filterDatesOnOrAfterEffectiveTomorrow(currSelectedDates);
        if (filtered.length !== currSelectedDates.length) {
            setCurrSelectedDates(filtered);
        }
    }, [currSelectedDates]);

    const handleSubmit = () => {
        handleOrder("address", selectedAddress);
        setOpen(false);

        // keep OrderContext in sync (optional but prevents drift)
        if (currSelectedDates.length > 0) {
            handleOrder("date", currSelectedDates);
            setSelectedDates(currSelectedDates);
        }

        if (cartLoaded && cartData && currSelectedDates.length > 0) {
            const address = selectedAddress !== "" ? selectedAddress : orderData.address;
            handleUpdateCart(currSelectedDates, address);
        }
    };

    return (
        <>
        {isOpen && (
            <div className={styles.Settings}>
            <div className={styles.SettingsBackground} onClick={() => setOpen(false)}></div>
            <div className={styles.SettingsCard}>
                <div className={styles.SettingsContent}>
                <p className={styles.SettingsTitle}>{t("date_address.title")}</p>
                <div className={styles.SettingsInputs}>
                    <AddressInput
                    onChange={setSelectedAddress}
                    value={selectedAddress !== "" ? selectedAddress : orderData.address}
                    addresses={adresses}
                    wrapperClassName={styles.AddressWrap}
                    className={styles.Address}
                    />
                    <DatePicker
                    onChange={setCurrSelectedDates}
                    selectedDates={currSelectedDates}
                    wrapperClassName={styles.DateWrap}
                    className={styles.Date}
                    />
                </div>
                </div>
                <SubmitButton text={t("buttons.confirm")} onClick={() => handleSubmit()} />
                <CloseButton onClick={() => setOpen(false)} className={styles.Close} />
            </div>
            </div>
        )}
        </>
    );
};

export default OrderSettingsPopup;
